import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Trophy } from "lucide-react";
import { toast } from "sonner";
import { setLeadStatus } from "@/lib/lead-pipeline.functions";
import {
  LeadDetailView,
  LEAD_STAGE_LABELS,
  type LeadDetailMessage,
  type LeadPipelineStatus,
} from "@/components/admin/LeadDetailView";

export const Route = createFileRoute("/admin/beskeder/$messageId")({
  head: () => ({
    meta: [
      { title: "Lead-detaljer – Admin – AutoXChange" },
      {
        name: "description",
        content:
          "Detaljeret lead-side med samtalehistorik, bil-oversigt, noter og pipeline-status for AutoXChange admin.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: LeadDetailPage,
});

function LeadDetailPage() {
  const { messageId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setStatusFn = useServerFn(setLeadStatus);

  const { data: message, isLoading, error } = useQuery({
    queryKey: ["lead-detail-message", messageId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("contact_messages")
        .select(
          "id, name, email, phone, message, created_at, pipeline_status, sale_value_dkk, car_id, car:cars(id, brand, model, variant, year, seo_slug, images, price, mileage, fuel_type, transmission, body_type, color, horsepower)",
        )
        .eq("id", messageId)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as unknown as LeadDetailMessage | null;
    },
  });

  const [wonDialog, setWonDialog] = useState<{ name: string } | null>(null);
  const [wonValue, setWonValue] = useState("");
  const [lostDialog, setLostDialog] = useState<{ name: string } | null>(null);

  const setStatusMutation = useMutation({
    mutationFn: (vars: { status: LeadPipelineStatus; saleValueDkk?: number }) =>
      setStatusFn({
        data: { contactId: messageId, status: vars.status, saleValueDkk: vars.saleValueDkk },
      }),
    onSuccess: (res, vars) => {
      queryClient.invalidateQueries({ queryKey: ["lead-detail-message", messageId] });
      queryClient.invalidateQueries({ queryKey: ["lead-details", messageId] });
      queryClient.invalidateQueries({ queryKey: ["admin-pipeline-messages"] });
      queryClient.invalidateQueries({ queryKey: ["admin-unread-count"] });
      if (vars.status === "won") {
        toast.success(
          `Markeret som vundet · Meta: ${res?.metaOk ? "✓" : "✗"} · GA4: ${res?.gaOk ? "✓" : "✗"}`,
        );
      } else {
        toast.success(`Status: ${LEAD_STAGE_LABELS[vars.status]}`);
      }
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Kunne ikke opdatere"),
  });

  const handleSetStatus = (status: LeadPipelineStatus) => {
    if (!message) return;
    if (status === "won") {
      setWonDialog({ name: message.name });
      setWonValue(String(message.sale_value_dkk ?? ""));
    } else if (status === "lost") {
      setLostDialog({ name: message.name });
    } else {
      setStatusMutation.mutate({ status });
    }
  };

  return (
    <div>
      <div className="mb-4 flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate({ to: "/admin/beskeder" })}
          className="text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft size={14} className="mr-1.5" />
          Tilbage til beskeder
        </Button>
      </div>

      {isLoading && (
        <div className="py-16 text-center text-muted-foreground">Indlæser lead…</div>
      )}

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-6 text-sm text-destructive">
          Kunne ikke indlæse lead: {error instanceof Error ? error.message : "Ukendt fejl"}
        </div>
      )}

      {!isLoading && !error && !message && (
        <div className="rounded-lg border border-dashed border-border p-10 text-center">
          <p className="text-foreground font-medium">Lead ikke fundet</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Beskeden eksisterer ikke længere, eller du har ikke adgang.
          </p>
          <Link
            to="/admin/beskeder"
            className="mt-4 inline-flex items-center gap-1.5 text-sm text-accent hover:underline"
          >
            <ArrowLeft size={12} />
            Tilbage til oversigten
          </Link>
        </div>
      )}

      {message && (
        <LeadDetailView message={message} onSetStatus={handleSetStatus} variant="page" />
      )}

      {/* Won dialog */}
      <Dialog
        open={!!wonDialog}
        onOpenChange={(o) => {
          if (!o) {
            setWonDialog(null);
            setWonValue("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Markér som vundet</DialogTitle>
            <DialogDescription>
              {wonDialog?.name} · Indtast salgsbeløb i DKK. Sender en offline
              Purchase-konvertering til Meta og GA4.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="mb-2 block text-sm font-medium">Salgsbeløb (DKK)</label>
            <Input
              type="number"
              inputMode="numeric"
              value={wonValue}
              onChange={(e) => setWonValue(e.target.value)}
              placeholder="f.eks. 250000"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWonDialog(null)}>
              Annullér
            </Button>
            <Button
              onClick={() => {
                const value = Number.parseInt(wonValue, 10);
                if (!Number.isFinite(value) || value < 0) {
                  toast.error("Indtast et gyldigt beløb");
                  return;
                }
                setStatusMutation.mutate({ status: "won", saleValueDkk: value });
                setWonDialog(null);
                setWonValue("");
              }}
              disabled={setStatusMutation.isPending}
            >
              <Trophy size={14} className="mr-1.5" />
              Bekræft vundet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lost confirm */}
      <AlertDialog open={!!lostDialog} onOpenChange={(o) => !o && setLostDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Markér som tabt?</AlertDialogTitle>
            <AlertDialogDescription>
              {lostDialog?.name} flyttes til Tabt. Du kan altid genåbne leadet.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annullér</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setStatusMutation.mutate({ status: "lost" });
                setLostDialog(null);
              }}
            >
              Markér som tabt
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
